const SUMMARY_MAX_LENGTH = 160;
const FULL_DETAIL_MIN_LENGTH = 200;

function looksLikeJson(value: string): boolean {
    const trimmed = value.trim();
    return trimmed.startsWith('{') || trimmed.startsWith('[');
}

function tryParseJson(value: string): unknown | null {
    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
}

function unescapeJsonString(fragment: string): string {
    try {
        return JSON.parse(`"${fragment}"`);
    } catch {
        return fragment;
    }
}

function extractErrorMessageFromText(text: string): string | null {
    const match = text.match(/"errorMessage"\s*:\s*"((?:[^"\\]|\\.)*)"/);
    if (match) {
        return unescapeJsonString(match[1]);
    }
    return null;
}

function findErrorMessage(value: unknown): string | null {
    if (value == null) {
        return null;
    }
    if (Array.isArray(value)) {
        for (const item of value) {
            const found = findErrorMessage(item);
            if (found) {
                return found;
            }
        }
        return null;
    }
    if (typeof value !== 'object') {
        return null;
    }

    const record = value as Record<string, unknown>;
    if (typeof record.errorMessage === 'string' && record.errorMessage.trim()) {
        return record.errorMessage.trim();
    }

    if (typeof record.message === 'string') {
        const nested = record.message.trim();
        const fromEmbedded = extractErrorMessageFromText(nested);
        if (fromEmbedded) {
            return fromEmbedded;
        }
        if (looksLikeJson(nested)) {
            const parsed = tryParseJson(nested);
            const fromParsed = findErrorMessage(parsed);
            if (fromParsed) {
                return fromParsed;
            }
        }
    }

    for (const child of Object.values(record)) {
        const found = findErrorMessage(child);
        if (found) {
            return found;
        }
    }

    return null;
}

function extractHumanReadableMessage(message: string): string {
    const trimmed = message.trim();
    if (!trimmed) {
        return '—';
    }

    if (looksLikeJson(trimmed)) {
        const parsed = tryParseJson(trimmed);
        if (parsed !== null) {
            const errorMessage = findErrorMessage(parsed);
            if (errorMessage) {
                return errorMessage;
            }
            if (typeof (parsed as Record<string, unknown>).message === 'string') {
                const topMessage = ((parsed as Record<string, unknown>).message as string).trim();
                const fromEmbedded = extractErrorMessageFromText(topMessage);
                if (fromEmbedded) {
                    return fromEmbedded;
                }
                if (topMessage && !looksLikeJson(topMessage)) {
                    return topMessage;
                }
            }
            return 'Grade sync failed (see full error)';
        }
    }

    const fromPlain = extractErrorMessageFromText(trimmed);
    if (fromPlain) {
        return fromPlain;
    }

    return trimmed;
}

function truncateSummary(value: string): string {
    if (value.length <= SUMMARY_MAX_LENGTH) {
        return value;
    }
    return `${value.slice(0, SUMMARY_MAX_LENGTH - 1)}…`;
}

export function extractGradeSyncLogSummary(message: string | null | undefined): string {
    if (!message?.trim()) {
        return '—';
    }
    return truncateSummary(extractHumanReadableMessage(message));
}

export function formatGradeSyncLogFullMessage(message: string | null | undefined): string {
    if (!message?.trim()) {
        return '—';
    }
    const trimmed = message.trim();
    if (looksLikeJson(trimmed)) {
        const parsed = tryParseJson(trimmed);
        if (parsed !== null) {
            return JSON.stringify(parsed, null, 2);
        }
    }
    return trimmed;
}

export function shouldShowFullErrorDetail(message: string | null | undefined): boolean {
    if (!message?.trim()) {
        return false;
    }
    const trimmed = message.trim();
    if (looksLikeJson(trimmed)) {
        return true;
    }
    if (trimmed.length > FULL_DETAIL_MIN_LENGTH) {
        return true;
    }
    return extractHumanReadableMessage(trimmed).length > SUMMARY_MAX_LENGTH;
}
